// Overpass API client — busca POIs próximos a um imóvel.
// Free, sem key, sem billing. Rate-limit honesto (~10 queries/min).
// Cache em localStorage por (lat, lng, raio) por 24h pra evitar refetch.

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
const CACHE_PREFIX = 'mf_nearby_v1_';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

// Mapeamento de tags OSM → categoria de display
const CATEGORY_MAP = [
  {
    id: 'mercados',
    label: 'Mercados e Padarias',
    icon: '🛒',
    match: (t) => (
      t.shop === 'supermarket' ||
      t.shop === 'convenience' ||
      t.shop === 'greengrocer' ||
      t.shop === 'bakery' ||
      t.shop === 'butcher'
    ),
  },
  {
    id: 'shopping',
    label: 'Shoppings e Lojas',
    icon: '🛍',
    match: (t) => t.shop === 'mall' || t.shop === 'department_store',
  },
  {
    id: 'restaurantes',
    label: 'Restaurantes e Bares',
    icon: '🍴',
    match: (t) => ['restaurant', 'cafe', 'bar', 'pub', 'fast_food', 'food_court'].includes(t.amenity),
  },
  {
    id: 'lazer',
    label: 'Cinema e Teatro',
    icon: '🎭',
    match: (t) => t.amenity === 'cinema' || t.amenity === 'theatre' || t.tourism === 'museum',
  },
  {
    id: 'parques',
    label: 'Parques e Áreas Verdes',
    icon: '🌳',
    match: (t) => t.leisure === 'park' || t.leisure === 'garden' || t.leisure === 'playground',
  },
  {
    id: 'esportes',
    label: 'Academias e Esportes',
    icon: '🏋',
    match: (t) => (
      t.amenity === 'gym' || t.amenity === 'fitness_centre' ||
      t.leisure === 'sports_centre' || t.leisure === 'fitness_centre' ||
      t.leisure === 'pitch' || t.leisure === 'swimming_pool'
    ),
  },
  {
    id: 'saude',
    label: 'Saúde',
    icon: '🏥',
    match: (t) => ['hospital', 'clinic', 'pharmacy', 'dentist', 'doctors'].includes(t.amenity),
  },
  {
    id: 'educacao',
    label: 'Educação',
    icon: '🎓',
    match: (t) => ['school', 'university', 'kindergarten', 'college'].includes(t.amenity),
  },
  {
    id: 'servicos',
    label: 'Bancos e Serviços',
    icon: '🏦',
    match: (t) => ['bank', 'atm', 'post_office'].includes(t.amenity),
  },
  {
    id: 'postos',
    label: 'Postos',
    icon: '⛽',
    match: (t) => t.amenity === 'fuel',
  },
];

// Distância em km via Haversine
function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function cacheKey(lat, lng, raio) {
  // arredonda lat/lng pra 3 casas (~110m) pra reutilizar cache de imóveis próximos
  const r = (n) => Number(n).toFixed(3);
  return `${CACHE_PREFIX}${r(lat)}_${r(lng)}_${raio}`;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch { return null; }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch {} // localStorage cheio, sem problema
}

/**
 * Busca lugares próximos via Overpass.
 * @param {number} lat
 * @param {number} lng
 * @param {number} raio metros (default 10000 = 10km)
 * @returns {Promise<Array<{ id, name, lat, lng, distance, category }>>}
 */
export async function fetchNearbyPlaces(lat, lng, raio = 10000) {
  if (!lat || !lng) return [];

  const key = cacheKey(lat, lng, raio);
  const cached = readCache(key);
  if (cached) return cached;

  const query = `
[out:json][timeout:25];
(
  nwr["amenity"~"^(restaurant|cafe|bar|pub|fast_food|food_court|cinema|theatre|gym|fitness_centre|school|university|kindergarten|college|hospital|clinic|pharmacy|dentist|doctors|bank|atm|post_office|fuel)$"](around:${raio},${lat},${lng});
  nwr["shop"~"^(supermarket|mall|convenience|department_store|bakery|butcher|greengrocer)$"](around:${raio},${lat},${lng});
  nwr["leisure"~"^(park|sports_centre|fitness_centre|playground|garden|pitch|swimming_pool)$"](around:${raio},${lat},${lng});
  nwr["tourism"="museum"](around:${raio},${lat},${lng});
);
out center 300;
`.trim();

  const res = await fetch(OVERPASS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: 'data=' + encodeURIComponent(query),
  });
  if (!res.ok) throw new Error(`Overpass ${res.status}`);
  const json = await res.json();

  const elements = json.elements || [];
  const places = [];
  const seenNames = new Set();

  for (const el of elements) {
    const tags = el.tags || {};
    const name = tags.name || tags['name:pt'] || tags['name:en'];
    if (!name) continue; // ignora sem nome

    // pega coord (node tem lat/lon, way/relation tem center)
    const plat = el.lat ?? el.center?.lat;
    const plng = el.lon ?? el.center?.lon;
    if (plat == null || plng == null) continue;

    const dist = distanceKm(lat, lng, plat, plng);

    // categoria
    const category = CATEGORY_MAP.find(c => c.match(tags));
    if (!category) continue;

    // dedup por nome+categoria
    const dedupKey = `${name.toLowerCase()}|${category.id}`;
    if (seenNames.has(dedupKey)) continue;
    seenNames.add(dedupKey);

    places.push({
      id: el.id,
      name,
      lat: plat,
      lng: plng,
      distance: dist,
      category: category.id,
      categoryLabel: category.label,
      icon: category.icon,
    });
  }

  // ordena por distância
  places.sort((a, b) => a.distance - b.distance);

  writeCache(key, places);
  return places;
}

/**
 * Agrupa places por categoria, mantendo ordem fixa do CATEGORY_MAP.
 */
export function groupByCategory(places) {
  const groups = new Map();
  for (const cat of CATEGORY_MAP) {
    groups.set(cat.id, { ...cat, items: [] });
  }
  for (const p of places) {
    const g = groups.get(p.category);
    if (g) g.items.push(p);
  }
  return Array.from(groups.values()).filter(g => g.items.length > 0);
}
